import { toast } from "react-toastify";
import { takeEvery, call, put, select } from "redux-saga/effects";

import {
  getScentsList,
  getShapesList,
  getStringsList,
  createCustomerCard,
  putCustomerCard,
  deleteCustomerCard,
  putShipmentRate,
  completePayment,
  completePaypalPayment,
  completeAmazonPayment,
} from "@/services/CheckoutService";

import { postDesignReorder } from "@/services/DesignerService";

import Customer from "@/redux/models/Customer";
import Checkout from "@/redux/models/Checkout";
import Order from "@/redux/models/Order";
import Design from "@/redux/models/Design";
import CustomShape from "@/redux/models/CustomShape";
import Profile from "@/redux/models/Profile";
import Items from "@/redux/models/Items";

import {
  getOrderRushOptions,
  putOrderRushOption,
  putOrderCouponCode,
  removeOrderCouponCode,
} from "@/services/OrderService";
import { clearUTMParams, removeCartOrder } from "@/utils/storage";
import { changeNumber } from "@/utils/updateHeaderCartQnty";

const { update } = Customer.types;
const {
  getShapes,
  getScents,
  updateCheckout,
  getStrings,
  addCard,
  updateCard,
  deleteCard,
  getRushList,
  selectRush,
  applyCouponCode,
  removeCouponCode,
  updateShipmentRate,
  clearAll,
  initCheckoutDesignReorder,
  reorderPreDefinedShape,
  payCreditCard,
  payPaypal,
  payAmazon,
} = Checkout.types;

function* getShapesSaga() {
  try {
    const shapes = yield call(getShapesList);

    yield put(
      updateCheckout({
        shapes: shapes.filter((shape) => !shape.slug.includes("custom")),
        customShape: shapes.find((shape) => shape.slug.includes("custom")),
      })
    );
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* getScentsSaga() {
  try {
    const scents = yield call(getScentsList);
    yield put(updateCheckout({ scents }));
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* getStringsSaga() {
  try {
    const strings = yield call(getStringsList);
    yield put(updateCheckout({ strings }));
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* addCardSaga(action) {
  try {
    const { onSuccess, onError, cardElement, stripe, ...cardData } = action.payload;

    const state = yield select();
    const user = state.Customer.itemsById[0];
    const result = yield call(stripe.createPaymentMethod, {
      type: "card",
      card: cardElement,
    });

    if (result.hasOwnProperty("error")) {
      toast.error(result.error.message || "Something went wrong!");
      if (onError) onError();

      return;
    }

    const resp = yield call(createCustomerCard, {
      ...cardData,
      payment_method_id: result.paymentMethod.id,
    });

    yield put(
      update({
        ...user,
        customer_id: user.id,
        cards: [...(user?.cards || []), resp],
      })
    );

    if (onSuccess) onSuccess(resp);
  } catch (err) {
    const { onError } = action.payload;
    if (onError) onError();

    toast.error(err.message || "Something went wrong!");
  }
}

function* updateCardSaga(action) {
  try {
    const { onSuccess, cardId, ...cardData } = action.payload;
    const state = yield select();
    const user = state.Customer.itemsById[0];
    const resp = yield call(putCustomerCard, cardData, cardId);

    yield put(
      update({
        ...user,
        customer_id: user.id,
        cards: [...(user?.cards || []), resp],
      })
    );

    toast.success("Card Updated");

    if (onSuccess) onSuccess(resp);
  } catch (err) {
    const { onError } = action.payload;
    if (onError) onError();

    toast.error(err.message || "Something went wrong!");
  }
}

function* deleteCardSaga(action) {
  try {
    const { id, cb } = action.payload;
    const state = yield select();
    const resp = yield call(deleteCustomerCard, id);

    const user = state.Customer.itemsById[0];

    yield put(
      update({
        ...user,
        customer_id: user.id,
        cards: resp,
      })
    );

    cb?.(resp);
    toast.success("Card Deleted");
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* getRushListSaga() {
  try {
    const order = yield select(Order.selectors.getOrder);
    if (order.id) {
      const rushList = yield call(getOrderRushOptions, order.id);
      yield put(updateCheckout({ rushList }));
    }
  } catch (err) {
    if (err?.response?.data?.detail === "You do not have permission to perform this action.") {
      yield put(Order.types.clear());
      yield put(Design.types.delete());
    }
    toast.error(err.message || err?.response?.data?.detail || "Something went wrong!");
  }
}

function* selectRushSaga(action) {
  try {
    const { rush } = action.payload;
    const order = yield select(Order.selectors.getOrder);
    const { ...orderUpdated } = yield call(putOrderRushOption, {
      id: order.id,
      rush,
    });

    yield put(Order.types.updateOrder(orderUpdated));
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* applyCouponSaga(action) {
  try {
    const { code } = action.payload;
    const order = yield select(Order.selectors.getOrder);
    const { ...orderUpdated } = yield call(putOrderCouponCode, {
      id: order.id,
      coupon_code: code,
    });

    yield put(Order.types.updateOrder(orderUpdated));
  } catch (err) {
    const { onError } = action.payload;
    if (onError) onError(err);
  } finally {
    const { callback } = action.payload;
    if (callback) callback();
  }
}

function* removeCouponSaga(action) {
  try {
    const { code } = action.payload;
    const order = yield select(Order.selectors.getOrder);
    const { ...orderUpdated } = yield call(removeOrderCouponCode, {
      id: order.id,
      coupon_code: code,
    });

    yield put(Order.types.updateOrder(orderUpdated));
  } catch (err) {
    const { onError } = action.payload;
    if (onError) onError(err);
  } finally {
    const { callback } = action.payload;
    if (callback) callback();
  }
}

function* updateShipmentRateSaga(action) {
  try {
    const { shipping_quote, shipping_method } = action.payload;

    const order = yield select(Order.selectors.getOrder);

    const { ...orderUpdated } = yield call(putShipmentRate, {
      order_id: order.id,
      shipping_quote,
      shipping_method,
    });

    yield put(Order.types.updateOrder(orderUpdated));
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  } finally {
    const { callback } = action.payload;
    if (callback) callback();
  }
}

function* clearAllSaga() {
  try {
    yield put(Order.types.delete());
    yield put(Design.types.delete());
    yield put(CustomShape.types.delete());
    yield put(Items.types.clear());
    yield put(Customer.types.delete());
    yield put(Profile.types.delete());
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* initCheckoutDesignReorderSaga(action) {
  try {
    const { id, add_hc, cb } = action.payload;
    yield put(
      reorderPreDefinedShape({
        id,
        add_hc,
        cb,
      })
    );
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* reorderPreDefinedShapeSaga(action) {
  try {
    const order = yield select(Order.selectors.getOrder);
    const { id, cb, add_hc } = action.payload;

    const { order_id, design_id } = yield call(postDesignReorder, id, add_hc, !order?.id);
    yield put(updateCheckout({ customCropType: "" }));
    yield put(CustomShape.types.delete());

    toast.success(
      "Don't worry your design is ready for you on the next page! We just need a little information first."
    );

    if (cb) cb(order?.id || order_id, design_id);
  } catch (err) {
    toast.error(err.message || "Something went wrong!");
  }
}

function* payCreditCardSaga(action) {
  const { paymentIntentId, cb, card, setIsLoading, stripe } = action.payload;

  try {
    setIsLoading?.(true);
    const order = yield select(Order.selectors.getOrder);

    yield call(completePayment, {
      id: order.id,
      amount: `${order?.pricing?.total}`,
      card: card?.id,
      payment_intent_id: paymentIntentId,
    });

    yield put(Order.types.clear());
    yield put(Design.types.delete());

    changeNumber(0);
    clearUTMParams();
    removeCartOrder();
    cb?.();
    toast.success("Payment Confirmed");
  } catch (err) {
    const paymentIntentId = err?.response?.data?.payment_intent_id;
    const secret = err?.response?.data?.client_secret;

    if (!secret) {
      setIsLoading?.(false);
      toast.error("Something went wrong!");
    } else {
      const res = yield call(stripe.confirmCardPayment, `${secret}`);

      if (res?.paymentIntent?.status === "succeeded") {
        yield put(payCreditCard({ ...action.payload, paymentIntentId }));
      } else {
        setIsLoading?.(false);
        toast.error(res?.error?.message || "Payment not confirmed");
      }
    }
  }
}

function* payAmazonSaga(action) {
  const { paymentIntentId, cb, setIsLoading } = action.payload;

  try {
    setIsLoading?.(true);
    const order = yield select(Order.selectors.getOrder);

    yield call(completeAmazonPayment, {
      id: order.id,
      amount: `${order?.pricing?.total}`,
      payment_intent_id: paymentIntentId,
    });

    yield put(Order.types.clear());
    yield put(Design.types.delete());

    clearUTMParams();
    removeCartOrder();
    cb?.();
    toast.success("Payment Confirmed");
  } catch {
    setIsLoading?.(false);
    toast.error("Something went wrong!");
  }
}

function* payPaypalSaga(action) {
  const { cb, setIsLoading, paypal } = action.payload;

  try {
    setIsLoading?.(true);
    const order = yield select(Order.selectors.getOrder);

    yield call(completePaypalPayment, {
      id: order.id,
      amount: `${order?.pricing?.total}`,
      paypal,
    });

    yield put(Order.types.clear());
    yield put(Design.types.delete());

    clearUTMParams();
    cb?.();
    toast.success("Payment Confirmed");
  } catch (err) {
    setIsLoading?.(false);
    toast.error(err?.error?.message || "Payment not confirmed");
  }
}

export default function* watchCheckout() {
  yield takeEvery(getShapes().type, getShapesSaga);
  yield takeEvery(getScents().type, getScentsSaga);
  yield takeEvery(getStrings().type, getStringsSaga);
  yield takeEvery(addCard().type, addCardSaga);
  yield takeEvery(updateCard().type, updateCardSaga);
  yield takeEvery(deleteCard().type, deleteCardSaga);
  yield takeEvery(getRushList().type, getRushListSaga);
  yield takeEvery(selectRush().type, selectRushSaga);
  yield takeEvery(applyCouponCode().type, applyCouponSaga);
  yield takeEvery(removeCouponCode().type, removeCouponSaga);
  yield takeEvery(updateShipmentRate().type, updateShipmentRateSaga);
  yield takeEvery(clearAll().type, clearAllSaga);
  yield takeEvery(initCheckoutDesignReorder().type, initCheckoutDesignReorderSaga);
  yield takeEvery(reorderPreDefinedShape().type, reorderPreDefinedShapeSaga);
  yield takeEvery(payCreditCard().type, payCreditCardSaga);
  yield takeEvery(payPaypal().type, payPaypalSaga);
  yield takeEvery(payAmazon().type, payAmazonSaga);
}
